import { PrizeType, WinType } from '@daymade/common-lib/dbEnums';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { ZPrizeCore, ZPrizeSelect, ZProductSelect } from '../prize/models';
import { ZPrizeWinSelect, ZSubmitPrizeChoiceRequest } from './models';

const ZPrizeWinCore = ZPrizeWinSelect.pick({
  id: true,
  ticketEntryId: true,
  status: true,
  prizeTierId: true,
}).extend({
  createdAt: z.coerce.date(),
  gameType: z.string(),
  prizeStructureCode: z.string(),
  prizeTierCode: z.string(),
  prizeTierType: z.string(),
  drawId: z.number(),
  drawName: z.string(),
});

const ZGetUnclaimedPrizeWinsResponse = ZPrizeWinCore.extend({
  expiresAt: z.coerce.date().nullable(),
  drawPrizeId: z.number().nullable(),
  drawPrizeSlug: z.string().nullable(),
  drawPrizeTitle: z.string().nullable(),
  drawPrizeSubheading: z.string().nullable(),
  drawPrizeImageLocation: z.string().nullable(),
}).array();
export type TGetUnclaimedPrizeWinsResponse = z.infer<
  typeof ZGetUnclaimedPrizeWinsResponse
>;

const ZGetClaimedPrizeWinsResponse = ZPrizeWinCore.extend({
  chosenAt: z.coerce.date().nullable(),
  chosenPrizeId: z.number().nullable(),
  chosenPrizeName: z.string().nullable(),
  chosenPrizeDescription: z.string().nullable(),
  chosenPrizeType: z.nativeEnum(PrizeType).nullable(),
  chosenPrizeImageLocation: z.string().nullable(),
  chosenPrizeProducts: ZProductSelect.pick({
    id: true,
    name: true,
    autoFulfilmentCode: true,
  })
    .extend({ prizeOrderProductId: z.string() })
    .array()
    .nullable(),
  isChosenByPlayer: z.boolean(),
  feedbackRating: z.coerce.number().nullable(),
  feedbackComment: z.string().nullable(),
}).array();
export type TGetClaimedPrizeWinsResponse = z.infer<
  typeof ZGetClaimedPrizeWinsResponse
>;

const ZGetTreatPrizeWinsResponse = ZPrizeWinCore.extend({
  chosenPrizeId: z.number().nullable(),
}).array();
export type TGetTreatPrizeWinsResponse = z.infer<
  typeof ZGetTreatPrizeWinsResponse
>;

const ZPrizeForRedemption = ZPrizeCore.extend({
  overviewBody: z.string().nullable(),
  needsManualFulfilment: z.boolean(),
});

const ZGetAvailablePrizesForRedemptionResponse = z.object({
  displayGroupPrizes: z.record(z.string(), ZPrizeForRedemption.array()),
  allAvailablePrizes: ZPrizeForRedemption.array(),
  winType: z.nativeEnum(WinType),
});
export type TGetAvailablePrizesForRedemptionResponse = z.infer<
  typeof ZGetAvailablePrizesForRedemptionResponse
>;

const ZGetPrizeBySlugForRedemptionResponse = ZPrizeSelect.pick({
  id: true,
  title: true,
  subheading: true,
  badge: true,
  slug: true,
  imageLocations: true,
  isFromPlayersSuggestion: true,
  type: true,
  overviewBody: true,
  whatsIncludedHeading: true,
  whatsIncludedBody: true,
  fulfilmentInfo: true,
  requiresDeliveryAddress: true,
  requiresEmailAddress: true,
  requiresPhoneNumber: true,
  additionalQuestionsForPlayer: true,
}).extend({
  products: ZProductSelect.pick({
    id: true,
    name: true,
    description: true,
    imageLocation: true,
    autoFulfilmentCode: true,
  }).array(),
});
export type TGetPrizeBySlugForRedemptionResponse = z.infer<
  typeof ZGetPrizeBySlugForRedemptionResponse
>;

const c = initContract();

export const authWinnerContract = c.router(
  {
    getUnclaimedPrizeWins: {
      method: 'GET',
      path: '/get-unclaimed-prize-wins',
      responses: {
        200: ZGetUnclaimedPrizeWinsResponse,
      },
      summary: 'Get unclaimed prize/treat wins of a player',
    },

    getClaimedPrizeWins: {
      method: 'GET',
      path: '/get-claimed-prize-wins',
      responses: {
        200: ZGetClaimedPrizeWinsResponse,
      },
      summary: 'Get claimed prize/treat wins of a player',
    },

    getTreatPrizeWins: {
      method: 'GET',
      path: '/get-treat-prize-wins',
      query: z.object({
        drawId: z.coerce.number().optional(),
      }),
      responses: {
        200: ZGetTreatPrizeWinsResponse,
      },
      summary: 'Get prize/treat wins of a player (with filter options)',
    },

    getAvailablePrizesForRedemption: {
      method: 'GET',
      path: '/get-available-prizes-for-redemption',
      query: z.object({
        prizeWinId: z.string(),
      }),
      responses: {
        200: ZGetAvailablePrizesForRedemptionResponse,
        400: z.object({
          message: z.string(),
        }),
      },
      summary:
        'Get prize/treat win details and available prizes for prize redemption',
    },

    getPrizeBySlugForRedemption: {
      method: 'GET',
      path: '/get-prize-by-slug-for-redemption',
      query: z.object({
        slug: z.string(),
      }),
      responses: {
        200: ZGetPrizeBySlugForRedemptionResponse,
        404: z.object({
          message: z.string(),
        }),
      },
      summary: 'Get a prize for prize redemption form',
    },

    submitPrizeChoice: {
      method: 'POST',
      path: '/submit-prize-choice',
      body: ZSubmitPrizeChoiceRequest,
      responses: {
        200: z.object({
          prizeOrderId: z.string(),
          prizeOrderProductIds: z.string().array(),
          autoFulfilmentCodes: z.string().array(),
        }),
        400: z.object({
          message: z.string(),
        }),
      },
      summary: 'Submit choice of prize for a prize win',
    },
  },
  {
    pathPrefix: '/auth',
    baseHeaders: z.object({
      'x-access-token': z.string(),
    }),
    commonResponses: {
      401: c.type<{ message: 'Unauthorized'; statusCode: 401 }>(),
      // 500: c.otherResponse({
      //   contentType: 'text/plain',
      //   body: z.literal('Server Error'),
      // }),
    },
  },
);
